/**
 * PlatformWorkspace — focused tabbed workspace for one author platform.
 *
 * Route-backed workspaces pass `activeTab`/`onTabChange` so the selected tab is
 * encoded in the URL. Legacy inline consumers can omit both and keep local tab
 * state while they are being retired.
 */

import React, { useMemo, useState } from 'react';
import { Alert, Box, Tab, Tabs } from '@mui/material';
import type { AuthorPlatformLite } from '@modules/content-plan/hooks/useAuthorPlatforms';
import type { AuthorBasePath } from '@modules/content-plan/services/AuthorPlatformService';
import { PlatformConfigEditor } from '@modules/content-plan/components/platforms/PlatformConfigEditor';
import { PlatformVoiceEditor } from '@modules/content-plan/components/platforms/PlatformVoiceEditor';
import { PlatformDesignEditor } from '@modules/content-plan/components/platforms/PlatformDesignEditor';
import { PlatformPostsTable } from '@modules/content-plan/components/platforms/PlatformPostsTable';
import { PerAuthorPostTemplatesLibrary } from '@modules/content-plan/components/author/PerAuthorPostTemplatesLibrary';

export const PLATFORM_WORKSPACE_TABS = ['config', 'voice', 'design', 'posts', 'templates'] as const;
export type PlatformWorkspaceTabKey = typeof PLATFORM_WORKSPACE_TABS[number];

export function normalizePlatformWorkspaceTab(value: string | null | undefined): PlatformWorkspaceTabKey {
  return PLATFORM_WORKSPACE_TABS.includes(value as PlatformWorkspaceTabKey)
    ? (value as PlatformWorkspaceTabKey)
    : 'config';
}

export interface PlatformWorkspaceProps {
  projectId: string;
  authorId: string;
  platform: AuthorPlatformLite;
  userId?: string;
  basePath?: AuthorBasePath;
  activeTab?: PlatformWorkspaceTabKey;
  onTabChange?: (tab: PlatformWorkspaceTabKey) => void;
  onTaskStarted?: (taskId: string, label: string) => void;
}

export const PlatformWorkspace: React.FC<PlatformWorkspaceProps> = ({
  projectId,
  authorId,
  platform,
  userId,
  basePath = 'authors',
  activeTab,
  onTabChange,
  onTaskStarted,
}) => {
  const [localTab, setLocalTab] = useState<PlatformWorkspaceTabKey>('config');
  const tab = activeTab ?? localTab;
  const platformId = platform.id;
  const tabs = useMemo<Array<{ value: PlatformWorkspaceTabKey; label: string }>>(() => [
    { value: 'config', label: 'Config' },
    { value: 'voice', label: 'Voice' },
    { value: 'design', label: 'Design' },
    { value: 'posts', label: 'Posts' },
    { value: 'templates', label: 'Templates' },
  ], []);

  const handleTabChange = (_event: React.SyntheticEvent, value: PlatformWorkspaceTabKey) => {
    if (!activeTab) setLocalTab(value);
    onTabChange?.(value);
  };

  return (
    <Box data-testid="content-plan-platform-workspace-body">
      <Tabs
        value={tab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          minHeight: 'auto',
        }}
        data-testid="content-plan-platform-workspace-tabs"
      >
        {tabs.map((item) => (
          <Tab
            key={item.value}
            value={item.value}
            label={item.label}
            sx={{ minHeight: 'auto', py: '10px' }}
            data-testid={`content-plan-platform-workspace-tab-${item.value}`}
          />
        ))}
      </Tabs>

      <Box sx={{ pt: 2 }}>
        {tab === 'config' && (
          <PlatformConfigEditor
            projectId={projectId}
            authorId={authorId}
            platform={platform}
            basePath={basePath}
          />
        )}
        {tab === 'voice' && (
          userId ? (
            <PlatformVoiceEditor
              projectId={projectId}
              authorId={authorId}
              platformId={platformId}
              userId={userId}
              basePath={basePath}
              onTaskStarted={onTaskStarted}
            />
          ) : (
            <Alert severity="info">Voice tools require a signed-in user.</Alert>
          )
        )}
        {tab === 'design' && (
          <PlatformDesignEditor
            projectId={projectId}
            authorId={authorId}
            platformId={platformId}
            basePath={basePath}
          />
        )}
        {tab === 'posts' && (
          userId ? (
            <PlatformPostsTable
              projectId={projectId}
              authorId={authorId}
              platformId={platformId}
              platformName={platform.displayName ?? platformId}
              profileUrl={platform.profileUrl}
              userId={userId}
              basePath={basePath}
              onTaskStarted={onTaskStarted}
            />
          ) : (
            <Alert severity="info">Post tools require a signed-in user.</Alert>
          )
        )}
        {tab === 'templates' && (
          <PerAuthorPostTemplatesLibrary
            projectId={projectId}
            authorId={authorId}
            platformId={platformId}
            basePath={basePath}
          />
        )}
      </Box>
    </Box>
  );
};

export default PlatformWorkspace;
